import { Fragment, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { useUserLookup } from "../../hooks/useUserLookup";
import { IWilmaUser } from "../../models/auth/WilmaUser";
import { ReturnDetail, ReturnEvent } from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { INBOX_DAY_TIME } from "../../utils/inboxDates";

/** `@Name` in a note bolded, matching users by forename the way the inbox's NoteText does. */
function NoteText({ body, users }: { body: string; users: IWilmaUser[] }) {
  const names = users.map((u) => u.forename).filter(Boolean).join("|");
  const parts = names ? body.split(new RegExp(`(@(?:${names}))`, "g")) : [body];
  return (
    <>
      {parts.map((part, i) => (part.startsWith("@") ? <strong key={i} className="font-semibold text-violet-900">{part}</strong> : <Fragment key={i}>{part}</Fragment>))}
    </>
  );
}

/** User ids for every `@Forename` in the note, so the API can tell the people mentioned. */
function mentionedIds(body: string, users: IWilmaUser[]): string[] {
  return users.filter((u) => u.forename && new RegExp(`@${u.forename}\\b`).test(body)).map((u) => u.id);
}

interface ReturnMessagesProps {
  detail: ReturnDetail;
  onSend: (body: string, note: boolean, mentions: string[]) => void;
  onDeleteNote: (eventId: string) => void;
  sending: boolean;
}

/** eBay return messages as bubbles (buyer left, us right) and lavender internal notes with @mentions. */
export default function ReturnMessages({ detail, onSend, onDeleteNote, sending }: ReturnMessagesProps) {
  const { t } = useTranslation("returns");
  const { users } = useUserLookup();
  const { userId: me } = useAuth();
  const userList = useMemo(() => users ?? [], [users]);
  const [text, setText] = useState("");
  const [note, setNote] = useState(detail.channel !== "ebay");
  const isEbay = detail.channel === "ebay";
  const conversationUrl = detail.links.conversationUrl;

  const send = () => {
    const body = text.trim();
    if (!body) return;
    onSend(body, note, note ? mentionedIds(body, userList) : []);
    setText("");
  };

  const bubble = (m: ReturnEvent) => {
    const buyer = m.kind === "BuyerMessage";
    const isNote = m.kind === "Note";
    const when = formatDateTime(m.at, INBOX_DAY_TIME);
    return (
      <div key={m.id} className={classNames("flex max-w-[72%] flex-col gap-1", buyer ? "items-start" : "items-end self-end")}>
        <div
          className={classNames(
            "whitespace-pre-line px-3.5 py-2.5 text-[13px] leading-relaxed",
            buyer ? "rounded-[12px_12px_12px_4px] border border-gray-200 bg-white text-gray-900" : isNote ? "rounded-[12px_12px_4px_12px] border border-violet-200 bg-violet-50 text-violet-900" : "rounded-[12px_12px_4px_12px] bg-primary-600 text-white"
          )}
        >
          {isNote && <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide">{t("Internal note")}</span>}
          {isNote ? <NoteText body={m.body ?? ""} users={userList} /> : m.body}
          {m.attachments && m.attachments.length > 0 && (
            <div className={classNames("flex flex-wrap gap-1.5", m.body && "mt-2")}>
              {m.attachments.map((a) => (
                <a key={a.url} href={a.url} target="_blank" rel="noreferrer">
                  <img loading="lazy" src={a.url} alt={a.name} className="h-20 w-20 rounded-lg object-cover" />
                </a>
              ))}
            </div>
          )}
        </div>
        <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
          {buyer ? detail.buyer.username : m.authorName ?? t("You")} · {when} · {buyer ? (isEbay ? t("eBay return message") : t("with the request")) : isNote ? t("not visible to buyer") : isEbay ? t("sent on eBay") : t("sent")}
          {isNote && m.authorUserId && m.authorUserId === me && (
            <button type="button" onClick={() => onDeleteNote(m.id)} disabled={sending} className="text-gray-400 hover:text-red-700">
              {t("Delete")}
            </button>
          )}
        </span>
      </div>
    );
  };

  return (
    <article className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
        <h2 className="text-[13px] font-semibold text-gray-900">{t("Messages on this return")}</h2>
        {conversationUrl && (
          <Link to={conversationUrl} className="text-[11px] font-semibold text-primary-700 hover:underline">
            {t("Open conversation in Messages")} →
          </Link>
        )}
      </div>
      <div className="flex flex-col gap-3 bg-gray-50 px-4 py-3.5">
        {detail.messages.length === 0 && <p className="text-[12px] text-gray-500">{t("No messages yet.")}</p>}
        {detail.messages.map(bubble)}
      </div>
      <div className="flex items-center gap-2 border-t border-gray-200 px-4 py-2.5">
        <label htmlFor="return-reply" className="sr-only">
          {t("Reply")}
        </label>
        <input
          id="return-reply"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          disabled={sending}
          placeholder={note ? t("Internal note — @Name to mention someone") : isEbay ? t("Reply to the buyer on eBay, or toggle Note…") : t("Message kept on the return, or toggle Note…")}
          className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-[13px] focus:border-primary-500 focus:ring-primary-500"
        />
        <label className="flex items-center gap-1.5 text-[12px] text-gray-700">
          <span className={classNames("relative inline-block h-4 w-7 rounded-full transition-colors", note ? "bg-violet-600" : "bg-gray-300")}>
            <input type="checkbox" checked={note} onChange={(e) => setNote(e.target.checked)} aria-label={t("Internal note")} className="absolute inset-0 m-0 h-full w-full cursor-pointer opacity-0" />
            <span className={classNames("absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all", note ? "left-3.5" : "left-0.5")} />
          </span>
          {t("Note")}
        </label>
        <button type="button" onClick={send} disabled={sending || !text.trim()} className="rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-[12px] font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50">
          {note ? t("Add note") : t("Send")}
        </button>
      </div>
    </article>
  );
}
